import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Book, Users, BarChart3, Plus, Edit, Eye, Folder, Download, Upload, Image, Database } from 'lucide-react';
import { useContent } from '../../contexts/SupabaseContentContext';
import { useAuth } from '../../contexts/SupabaseAuthContext';
import WordPressImport from '../../components/WordPressImport';

const AdminDashboard: React.FC = () => {
  const { blogPosts, docPosts, categories, generateSitemap } = useContent();
  const { user, signOut } = useAuth();
  const [showWordPressImport, setShowWordPressImport] = useState(false);

  // Set page title
  React.useEffect(() => {
    document.title = 'Admin Dashboard - VMon Cloud';
    return () => {
      document.title = 'VMon Cloud - Dịch vụ VPS và Hosting chất lượng cao';
    };
  }, []);

  const stats = [
    {
      name: 'Tổng bài viết Blog',
      value: blogPosts.length,
      icon: FileText,
      color: 'bg-blue-500'
    },
    {
      name: 'Tài liệu',
      value: docPosts.length,
      icon: Book,
      color: 'bg-green-500'
    },
    {
      name: 'Danh mục',
      value: categories.length,
      icon: Folder,
      color: 'bg-purple-500'
    },
    {
      name: 'Bài viết đã xuất bản',
      value: blogPosts.filter(post => post.published).length,
      icon: Eye,
      color: 'bg-yellow-500'
    }
  ];

  // Tạo danh sách bài viết gần đây với thông tin loại bài viết
  const recentPosts = [
    ...blogPosts.map(post => ({ ...post, type: 'blog' as const })),
    ...docPosts.map(doc => ({ ...doc, type: 'doc' as const }))
  ]
    .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())
    .slice(0, 5);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-4">Dashboard</h1>
              <p className="text-gray-600">Chào mừng trở lại, {user?.email}!</p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={generateSitemap}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors inline-flex items-center"
              >
                <Download className="h-4 w-4 mr-2" />
                Tải Sitemap
              </button>
              <button
                onClick={signOut}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors"
              >
                Đăng xuất
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {stats.map((stat, index) => (
            <div key={index} className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className={`${stat.color} rounded-lg p-3 mr-4`}>
                  <stat.icon className="h-6 w-6 text-white" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">{stat.name}</p>
                  <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* WordPress Import Toggle */}
        <div className="mb-8 bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Import từ WordPress</h3>
            <button
              onClick={() => setShowWordPressImport(!showWordPressImport)}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              {showWordPressImport ? 'Ẩn công cụ' : 'Hiển thị công cụ'}
            </button>
          </div>
          
          {showWordPressImport && <WordPressImport />}
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Quản lý Blog</h3>
            <div className="space-y-3">
              <Link
                to="/vmon-admin/editor/blog"
                className="flex items-center w-full p-3 text-left bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
              >
                <Plus className="h-5 w-5 text-blue-600 mr-3" />
                <span className="text-blue-600 font-medium">Tạo bài viết mới</span>
              </Link>
              <Link
                to="/vmon-admin/blogs"
                className="flex items-center w-full p-3 text-left bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <FileText className="h-5 w-5 text-gray-600 mr-3" />
                <span className="text-gray-600 font-medium">Quản lý bài viết</span>
              </Link>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Quản lý Tài liệu</h3>
            <div className="space-y-3">
              <Link
                to="/vmon-admin/editor/doc"
                className="flex items-center w-full p-3 text-left bg-green-50 hover:bg-green-100 rounded-lg transition-colors"
              >
                <Plus className="h-5 w-5 text-green-600 mr-3" />
                <span className="text-green-600 font-medium">Tạo tài liệu mới</span>
              </Link>
              <Link
                to="/vmon-admin/docs"
                className="flex items-center w-full p-3 text-left bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <Book className="h-5 w-5 text-gray-600 mr-3" />
                <span className="text-gray-600 font-medium">Quản lý tài liệu</span>
              </Link>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Quản lý Hệ thống</h3>
            <div className="space-y-3">
              <Link
                to="/vmon-admin/categories"
                className="flex items-center w-full p-3 text-left bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors"
              >
                <Folder className="h-5 w-5 text-purple-600 mr-3" />
                <span className="text-purple-600 font-medium">Quản lý danh mục</span>
              </Link>
              <Link
                to="/vmon-admin/image-manager"
                className="flex items-center w-full p-3 text-left bg-indigo-50 hover:bg-indigo-100 rounded-lg transition-colors"
              >
                <Image className="h-5 w-5 text-indigo-600 mr-3" />
                <span className="text-indigo-600 font-medium">Quản lý hình ảnh</span>
              </Link>
              <Link
                to="/vmon-admin/content-updater"
                className="flex items-center w-full p-3 text-left bg-pink-50 hover:bg-pink-100 rounded-lg transition-colors"
              >
                <Database className="h-5 w-5 text-pink-600 mr-3" />
                <span className="text-pink-600 font-medium">Cập nhật hàng loạt</span>
              </Link>
              <button
                onClick={generateSitemap}
                className="flex items-center w-full p-3 text-left bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <Download className="h-5 w-5 text-gray-600 mr-3" />
                <span className="text-gray-600 font-medium">Tạo Sitemap</span>
              </button>
            </div>
          </div>
        </div>

        {/* Recent Posts */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Bài viết mới nhất</h3>
          </div>
          <div className="p-6">
            {recentPosts.length === 0 ? (
              <p className="text-gray-500 text-center py-4">Chưa có bài viết nào</p>
            ) : (
              <div className="space-y-4">
                {recentPosts.map(post => (
                  <div key={`${post.type}-${post.id}`} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-4">
                      <div className={`w-2 h-2 rounded-full ${post.type === 'doc' ? 'bg-green-500' : 'bg-blue-500'}`}></div>
                      <div>
                        <h4 className="font-medium text-gray-900">{post.title}</h4>
                        <p className="text-sm text-gray-500">
                          {post.type === 'doc' ? 'Tài liệu' : 'Blog'} • 
                          Cập nhật: {new Date(post.updated_at).toLocaleDateString('vi-VN')}
                        </p>
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <Link
                        to={`/vmon-admin/editor/${post.type}/${post.id}`}
                        className="text-blue-600 hover:text-blue-800"
                        title="Chỉnh sửa"
                      >
                        <Edit className="h-4 w-4" />
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminDashboard;